import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Code2, Wrench, ExternalLink, Clock, CheckCircle, AlertCircle, Rocket } from 'lucide-react';
import FadingLine from '../Components/FadingLine';
import Footer from '../Components/Footer';
import ErrorPage from './Projects/StupidDesigns/ErrorPage'; 
import SignupPage from './Projects/StupidDesigns/SignupPage';
import Chatbot from './Projects/AIProjects/Chatbot';

export const projectSections = [
  {
    id: 1,
    title: "AI Projects",
    description: "Projects built with and for AI — exploring the power of artificial intelligence, automation, and machine learning. These projects leverage AI tools, APIs, or concepts to create smart, adaptive, or generative experiences.",
    icon: <Rocket className="w-6 h-6" />,
    color: "from-blue-500 to-green-500",
    route: "/projects/AIProjects",
    projects: [
      { id: 1, name: "AI chatbot - Indra", tech: "React.js, Python", status: "completed", component: Chatbot },
      { id: 2, name: "Computer Vision - Object Detector", tech: "TBD", status: "planning" },
      { id: 3, name: "AI Wrapped Project", tech: "TBD", status: "planning" },
      { id: 4, name: "News summariser bot", tech: "TBD", status: "in-progress" },
      { id: 5, name: "TBD", tech: "TBD", status: "planning" },
      { id: 6, name: "TBD", tech: "TBD", status: "planning" }
    ]
  },
  {
    id: 2,
    title: "Personal Projects",
    description: "Projects focused on tackling everyday challenges through thoughtful, functional design. These tools are built to solve real problems in practical, user-friendly ways, helping me (and hopefully others) live a little more efficiently.",
    icon: <Wrench className="w-6 h-6" />,
    color: "from-green-500 to-blue-500",
    route: "/projects/PersonalProjects",
    projects: [
      { id: 1, name: "Workout Tracker", tech: "TBD", status: "planning" },
      { id: 2, name: "System Tracker", tech: "React, Local Storage", status: "in-progress"},
      { id: 3, name: "TBD", tech: "TBD", status: "planning" },
      { id: 4, name: "TBD", tech: "TBD", status: "planning" },
      { id: 5, name: "TBD", tech: "TBD", status: "planning" },
      { id: 6, name: "TBD", tech: "TBD", status: "planning" }
    ]
  },
  {
    id: 3,
    title: "Stupid Designs",
    description: "A collection of intentionally silly, impractical, or just plain ridiculous designs. These are here to make you laugh, groan, or wonder 'why?!' — because sometimes, bad ideas are the most fun.",
    icon: <Code2 className="w-6 h-6" />,
    color: "from-green-500 to-blue-500",
    route: "/projects/StupidDesigns",
    projects: [
      { id: 1, name: "Signup Page", tech: "React.js, tailwind", status: "completed", component: SignupPage},
      { id: 2, name: "404 Error Page", tech: "React.js, tailwind, framer motion", status: "completed", component: ErrorPage },
      { id: 3, name: "TBD", tech: "TBD", status: "planning"},
      { id: 4, name: "TBD", tech: "TBD", status: "planning"},
      { id: 5, name: "TBD", tech: "TBD", status: "planning" },
      { id: 6, name: "TBD", tech: "TBD", status: "planning" }
    ]
  }
];

export function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function Projects() {
  const [hoveredProject, setHoveredProject] = useState(null);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in-progress':
        return <Clock className="w-4 h-4 text-orange-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      default:
        return 'Planning';
    }
  };

  // Sticky Section component
  const StickyProjectSection = ({ section }) => (
    <section
      className={`
        pt-4
        
        /* IMPORTANT: Apply sticky behavior ONLY from medium screens (md) and up */
        md:sticky md:top-[80px] 
        
        /* IMPORTANT: Apply min-height only from medium screens (md) and up */
        md:min-h-[calc(100vh-180px)] 

        /* Centering applies only from medium screens (md) and up */
        flex flex-col md:justify-center md:items-center 
        
        bg-gray-900 ${section.color} transition-colors duration-500
      `}
      aria-label={section.title}
      tabIndex={0}
    >
      <div className="max-w-7xl mx-auto w-full px-4 text-center">
        {/* Section Header with responsive font sizes */}
        <div className={`
            inline-flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r ${section.color} 
            text-white mb-4 
            md:mb-6 
            text-center 
          `}>
          {section.icon}
          <h2 className="text-2xl md:text-3xl font-bold">{section.title}</h2> 
        </div>
        <p className="text-gray-200 text-base md:text-lg mb-4 max-w-2xl mx-auto">{section.description}</p> 
        <div className="flex justify-center mb-4">
          {/* Your 'View All' Link (currently commented out) */}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">
          {section.projects.map((project) => (
            <div
              key={`${section.id}-${project.id}`} 
              className="relative bg-gray-800 rounded-xl p-4 sm:p-6 hover:bg-gray-700 transition-all duration-300 group cursor-pointer" 
              onMouseEnter={() => setHoveredProject(`${section.id}-${project.id}`)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>{getStatusIcon(project.status)}{getStatusText(project.status)}</span>
                {hoveredProject === `${section.id}-${project.id}` && (
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                )}
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-1 group-hover:text-green-400 transition-colors">{project.name}</h3> 
              <p className="text-gray-400 text-sm mb-2 group-hover:text-gray-300 transition-colors">{project.tech}</p>
              <Link
                to={`/projects/${slugify(section.title)}/${slugify(project.name)}`}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors group-hover:text-green-400"
              >
                View Details
                <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="sticky top-0 z-20 bg-gray-900"> 
        <div className="text-center pt-6 animate-fadeIn">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">My Projects</h1>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto mb-4">A collection of my work for and with AI, problem-solving solutions and stupid designs</p> 
        </div>
        <FadingLine />
      </div>
      <div className="w-full flex flex-col">
        {projectSections.map((section) => (
          <StickyProjectSection
            key={section.id} 
            section={section}
          />
        ))}
      </div>
      <Footer />
    </div>
  );
}

export default Projects;